import { format, isValid, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz/formatInTimeZone';

/**
 * Format an ISO date/datetime string as DD/MM/YYYY. Returns '' for empty input
 * and the original string when unparseable, so a render never throws.
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  const parsed = parseISO(value);
  if (!isValid(parsed)) {
    return value;
  }

  return format(parsed, 'dd/MM/yyyy');
}

/**
 * Format an ISO datetime string as "dd MMM yyyy, HH:mm" (e.g. "30 Jun 2026,
 * 17:43"). Follows the same fallback convention as {@link formatDate}: empty
 * input returns '', unparseable input returns the raw string.
 */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  const parsed = parseISO(value);
  if (!isValid(parsed)) {
    return value;
  }

  return format(parsed, 'dd MMM yyyy, HH:mm');
}

/**
 * Timezone the permit start/end datetimes are rendered in (CWL-141).
 *
 * CORE currently forwards SAP's Europe/London wall-clock permit times without
 * converting them, so they arrive labelled as UTC (a 14:00 London permit end
 * ships as `14:00:00.000Z`) — see ignite-core
 * `process-workorder-history.action.ts:147,149`, where `startDate`/`endDate`
 * skip the `parseSapDateNZ`/`sapLondonToUtc` conversion that the adjacent
 * `actualStart` correctly applies. Rendering in UTC therefore shows the same
 * digits CORE and Street Manager show, which is what the business asked for.
 *
 * This is a COMPENSATING fix: once CORE is corrected the instants become
 * genuinely UTC and this must change to `clientConfig.features.schedulingTimeZone`
 * ('Europe/London'), or the permit times will read an hour early. Tracked by the
 * CORE follow-up raised alongside CWL-141.
 */
const PERMIT_DISPLAY_TIME_ZONE = 'UTC';

/**
 * Format a permit datetime as "dd MMM yyyy, HH:mm" in
 * {@link PERMIT_DISPLAY_TIME_ZONE} — never in the browser's local zone, which
 * is what made Works Lite show a permit end an hour later than CORE (CWL-141).
 *
 * Deliberately separate from {@link formatDateTime}: only the permit start/end
 * rows are in scope for CWL-141, and the other `formatDateTime` call sites must
 * keep their current behaviour.
 */
export function formatPermitDateTime(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  const parsed = parseISO(value);
  if (!isValid(parsed)) {
    return value;
  }

  return formatInTimeZone(parsed, PERMIT_DISPLAY_TIME_ZONE, 'dd MMM yyyy, HH:mm');
}
